import { Client, type Room } from "@colyseus/sdk";
import {
  ROOM_MESSAGES,
  type InstanceState,
  type RegionTransitionHandoffRequest,
  type RegionTransitionHandoffResponse,
} from "@ninja/contracts";
import {
  GameLoadingOverlay,
  type GameLoadingProgress,
} from "../ui/GameLoadingOverlay.js";

export type GameMode = "pve" | "pvp";
export type GameRoom = Room<InstanceState>;

export interface GameMountProgress {
  onAssetProgress(progress: number): void;
  onFirstFrame(): void;
}

export interface GameSessionLifecycleDependencies {
  request: <T>(path: string, init?: RequestInit) => Promise<T>;
  createClient: () => Client;
  loading: GameLoadingOverlay;
  getRoom: () => GameRoom | null;
  setRoom: (room: GameRoom | null) => void;
  mountGame: (
    room: GameRoom,
    mode: GameMode,
    selectedMapId: string | undefined,
    progress: GameMountProgress,
  ) => Promise<void>;
  destroyGame: () => void;
  setLeavingGame: (leaving: boolean) => void;
  markRoomExitIntentional: (room: GameRoom) => void;
  setPlaying: (playing: boolean) => void;
  saveRoomToken: (token: string) => void;
  clearRoomToken: () => void;
  getPendingRegionTransition: () => RegionTransitionRequest | null;
  savePendingRegionTransition: (transition: RegionTransitionRequest) => void;
  clearPendingRegionTransition: () => void;
  setEntryButtonDisabled: (
    mode: GameMode,
    disabled: boolean,
    button?: HTMLButtonElement,
  ) => void;
  showEntryError: (message: string) => void;
  showTransitionError: (message: string) => void;
  showDashboard: () => Promise<void>;
  setNotice: (message: string) => void;
}

interface RoomEntryResponse {
  roomName: string;
  mode: GameMode;
  instanceId: string;
  mapId: string;
  ticket: string;
}

interface RegionTransitionEntryResponse extends RoomEntryResponse {
  spawnId: string;
  regionTransitionId: string;
}

export interface RegionTransitionRequest {
  proof: string;
  fromMapId: string;
  toMapId: string;
  spawnId: string;
  accessId?: string;
  startedAt?: number;
}

const MAP_RUNTIME_TIMEOUT_MS = 5_000;
const REGION_TRANSITION_RETRY_WINDOW_MS = 110_000;
const HANDOFF_REQUEST_TIMEOUT_MS = 5_000;
const ROOM_RECONNECT_POLL_MS = 250;
const DESTINATION_RETRY_DELAY_MS = 750;
const PHASER_PROGRESS_START = 0.3;
const PHASER_PROGRESS_END = 0.92;

export class GameSessionLifecycle {
  private entryInProgress = false;
  private villageExitInProgress = false;

  constructor(
    private readonly dependencies: GameSessionLifecycleDependencies,
  ) {}

  async returnToVillage(): Promise<void> {
    if (this.villageExitInProgress) return;
    this.villageExitInProgress = true;

    const room = this.dependencies.getRoom();
    this.dependencies.setLeavingGame(true);
    this.dependencies.clearRoomToken();
    this.dependencies.clearPendingRegionTransition();

    if (room) {
      this.dependencies.markRoomExitIntentional(room);
      this.beginIntentionalRoomExit(room);
      if (this.dependencies.getRoom() === room) this.dependencies.setRoom(null);
    }

    this.dependencies.destroyGame();
    this.dependencies.setPlaying(false);

    try {
      await this.dependencies.showDashboard();
    } catch (error) {
      console.error("Could not return to the village after leaving the game", {
        roomId: room?.roomId,
        sessionId: room?.sessionId,
        error,
      });
      this.dependencies.setNotice(
        "The game session ended, but the village could not be loaded. Try refreshing.",
      );
    } finally {
      this.dependencies.setLeavingGame(false);
      this.villageExitInProgress = false;
    }
  }

  async enterGame(
    mode: GameMode,
    mapId: string,
    clickedButton?: HTMLButtonElement,
  ): Promise<void> {
    if (this.entryInProgress) return;
    this.entryInProgress = true;
    this.dependencies.setEntryButtonDisabled(mode, true, clickedButton);
    this.dependencies.loading.begin(
      "Preparing your instance",
      "Requesting an authoritative game room.",
    );

    let joiningRoom: GameRoom | null = null;
    let mountStarted = false;
    try {
      const response =
        mode === "pve"
          ? await this.dependencies.request<RoomEntryResponse>(
              "/instances/pve",
              { method: "POST", body: JSON.stringify({ mapId }) },
            )
          : await this.dependencies.request<RoomEntryResponse>(
              "/matches/pvp/queue",
              { method: "POST", body: "{}" },
            );
      this.update(
        0.14,
        "Connecting to the game room",
        "Joining your private runtime.",
      );

      joiningRoom = await this.dependencies
        .createClient()
        .joinOrCreate<InstanceState>(response.roomName, {
          mode: response.mode,
          instanceId: response.instanceId,
          mapId: response.mapId,
          ticket: response.ticket,
        });
      this.dependencies.setRoom(joiningRoom);
      this.update(
        0.24,
        "Receiving world data",
        "Waiting for the room's authoritative map state.",
      );
      await this.waitForMapRuntime(joiningRoom);
      this.dependencies.saveRoomToken(joiningRoom.reconnectionToken);
      mountStarted = true;
      await this.mount(joiningRoom, mode, response.mapId);
    } catch (error) {
      if (joiningRoom) {
        this.dependencies.setLeavingGame(true);
        this.dependencies.clearRoomToken();
        if (this.dependencies.getRoom() === joiningRoom)
          this.dependencies.setRoom(null);
        this.dependencies.destroyGame();
        this.dependencies.setPlaying(false);
        try {
          await joiningRoom.leave();
        } catch {
          // The room may already be disconnected after a failed initialization.
        } finally {
          this.dependencies.setLeavingGame(false);
        }
      }
      this.dependencies.clearRoomToken();
      this.dependencies.setEntryButtonDisabled(mode, false, clickedButton);
      this.dependencies.loading.hide();
      if (mountStarted) await this.dependencies.showDashboard();
      this.dependencies.showEntryError(this.formatEntryError(error));
    } finally {
      this.entryInProgress = false;
    }
  }

  async transitionToRegionFloor(
    sourceRoom: GameRoom,
    transition: RegionTransitionRequest,
  ): Promise<boolean> {
    const pendingTransition = {
      ...transition,
      startedAt: transition.startedAt ?? Date.now(),
    };
    this.dependencies.savePendingRegionTransition(pendingTransition);
    this.dependencies.setLeavingGame(true);
    let sourceLeft = false;
    let destinationRoom: GameRoom | null = null;
    this.dependencies.loading.begin(
      "Traveling to the next area",
      "Preparing the destination map and its runtime.",
    );

    try {
      await this.requestRegionTransitionEntry(pendingTransition);
      this.update(
        0.16,
        "Securing the current area",
        "Saving your squad before opening the destination.",
      );

      const handoffCommitted = await this.commitSourceHandoff(
        sourceRoom,
        pendingTransition,
      );
      sourceLeft = true;
      if (handoffCommitted) await this.closeRoomWithoutReconnect(sourceRoom);
      this.dependencies.clearRoomToken();
      if (this.dependencies.getRoom() === sourceRoom)
        this.dependencies.setRoom(null);
      this.dependencies.destroyGame();
      this.dependencies.setPlaying(false);

      this.update(
        0.24,
        "Connecting to the next area",
        "Opening a fresh destination ticket.",
      );
      destinationRoom =
        await this.joinRegionTransitionDestination(pendingTransition);
      this.dependencies.setRoom(destinationRoom);
      this.dependencies.saveRoomToken(destinationRoom.reconnectionToken);
      this.update(
        0.29,
        "Receiving destination data",
        "Waiting for the new map state.",
      );
      await this.waitForMapRuntime(destinationRoom);
      this.dependencies.saveRoomToken(destinationRoom.reconnectionToken);
      this.dependencies.clearPendingRegionTransition();
      await this.mount(destinationRoom, "pve", pendingTransition.toMapId);
      return true;
    } catch (error) {
      console.error("Could not transition to the next region floor", error);
      if (destinationRoom) {
        this.dependencies.setRoom(destinationRoom);
        this.dependencies.saveRoomToken(destinationRoom.reconnectionToken);
      }
      if (!sourceLeft) {
        try {
          sourceRoom.send("region.transition.cancel", {
            proof: pendingTransition.proof,
          });
        } catch {
          // A disconnected source room can already have discarded the attempt.
        }
        this.dependencies.clearPendingRegionTransition();
        this.dependencies.showTransitionError((error as Error).message);
      } else {
        if (!destinationRoom) {
          this.dependencies.setRoom(null);
          this.dependencies.clearRoomToken();
        }
        if (this.dependencies.getRoom() !== destinationRoom)
          this.dependencies.destroyGame();
        this.dependencies.setPlaying(false);
        await this.dependencies.showDashboard();
        this.dependencies.setNotice(
          `Your area transition is saved and can be resumed: ${(error as Error).message}`,
        );
      }
      return false;
    } finally {
      this.dependencies.setLeavingGame(false);
      this.dependencies.loading.hide();
    }
  }

  async resumeRoom(token: string): Promise<boolean> {
    this.dependencies.loading.begin(
      "Restoring your session",
      "Reconnecting to your active game room.",
    );
    let room: GameRoom | null = null;
    try {
      const pendingTransition = this.dependencies.getPendingRegionTransition();
      room = await this.dependencies
        .createClient()
        .reconnect<InstanceState>(token);
      this.update(
        0.2,
        "Receiving world data",
        "Restoring the room's authoritative map state.",
      );
      await this.waitForMapRuntime(room);
      if (room.state.mode !== "pve" && room.state.mode !== "pvp")
        throw new Error("The saved room has an unknown game mode.");
      this.dependencies.setRoom(room);
      this.dependencies.saveRoomToken(room.reconnectionToken);
      if (pendingTransition) {
        if (room.state.mapId === pendingTransition.toMapId) {
          this.dependencies.clearPendingRegionTransition();
          await this.mount(room, "pve", room.state.mapId);
          return true;
        }
        if (room.state.mapId === pendingTransition.fromMapId) {
          const transitionResumed = await this.transitionToRegionFloor(
            room,
            pendingTransition,
          );
          if (transitionResumed) return true;
          if (room.connection?.isOpen && this.dependencies.getRoom() === room) {
            await this.mount(room, "pve", pendingTransition.fromMapId);
            return true;
          }
          return false;
        }
        throw new Error("The saved transition no longer matches this room.");
      }
      await this.mount(room, room.state.mode, room.state.mapId);
      return true;
    } catch {
      if (room) {
        this.dependencies.setLeavingGame(true);
        if (this.dependencies.getRoom() === room)
          this.dependencies.setRoom(null);
        this.dependencies.destroyGame();
        this.dependencies.setPlaying(false);
        await this.closeRoomWithoutReconnect(room);
        this.dependencies.setLeavingGame(false);
      }
      this.dependencies.clearRoomToken();
      this.dependencies.loading.hide();
      return false;
    }
  }

  async resumePendingRegionTransition(): Promise<boolean> {
    const transition = this.dependencies.getPendingRegionTransition();
    if (!transition) return false;
    if (
      transition.startedAt !== undefined &&
      Date.now() - transition.startedAt >= 120_000
    ) {
      this.dependencies.clearPendingRegionTransition();
      this.dependencies.clearRoomToken();
      return false;
    }
    this.dependencies.loading.begin(
      "Resuming your area transition",
      "Reconnecting to the destination prepared for your squad.",
    );
    this.dependencies.setLeavingGame(true);
    let destinationRoom: GameRoom | null = null;
    try {
      destinationRoom = await this.joinRegionTransitionDestination(transition);
      this.dependencies.setRoom(destinationRoom);
      this.dependencies.saveRoomToken(destinationRoom.reconnectionToken);
      await this.waitForMapRuntime(destinationRoom);
      this.dependencies.clearPendingRegionTransition();
      await this.mount(destinationRoom, "pve", transition.toMapId);
      return true;
    } catch (error) {
      if (destinationRoom) {
        this.dependencies.setRoom(destinationRoom);
        this.dependencies.saveRoomToken(destinationRoom.reconnectionToken);
      } else {
        this.dependencies.clearRoomToken();
      }
      this.dependencies.setPlaying(false);
      this.dependencies.setNotice(
        `Your area transition is saved and can be resumed: ${(error as Error).message}`,
      );
      return false;
    } finally {
      this.dependencies.setLeavingGame(false);
      this.dependencies.loading.hide();
    }
  }

  private requestRegionTransitionEntry(
    transition: RegionTransitionRequest,
  ): Promise<RegionTransitionEntryResponse> {
    return this.dependencies.request<RegionTransitionEntryResponse>(
      "/instances/pve/transition",
      {
        method: "POST",
        body: JSON.stringify({ proof: transition.proof }),
      },
    );
  }

  private async commitSourceHandoff(
    room: GameRoom,
    transition: RegionTransitionRequest,
  ): Promise<boolean> {
    const deadline =
      (transition.startedAt ?? Date.now()) + REGION_TRANSITION_RETRY_WINDOW_MS;
    let sourceEnded = false;
    const onLeave = (): void => {
      sourceEnded = true;
    };
    room.onLeave(onLeave);
    try {
      while (Date.now() < deadline) {
        if (sourceEnded) return false;
        if (!room.connection?.isOpen) {
          if (!room.reconnection.isReconnecting) return false;
          await this.delay(
            Math.min(ROOM_RECONNECT_POLL_MS, deadline - Date.now()),
          );
          continue;
        }

        try {
          const payload: RegionTransitionHandoffRequest = {
            proof: transition.proof,
          };
          const result = await room.request<
            RegionTransitionHandoffRequest,
            RegionTransitionHandoffResponse
          >(ROOM_MESSAGES.regionTransitionCommit, payload, {
            timeout: HANDOFF_REQUEST_TIMEOUT_MS,
          });
          if (result.committed) return true;
        } catch (error) {
          if (sourceEnded) return false;
          const message =
            error instanceof Error ? error.message : String(error);
          if (
            message.includes("REGION_TRANSITION_HANDOFF_INVALID") ||
            message.includes("REGION_TRANSITION_HANDOFF_EXPIRED")
          )
            throw error;
          await this.delay(
            Math.min(DESTINATION_RETRY_DELAY_MS, deadline - Date.now()),
          );
        }
      }
      if (
        sourceEnded ||
        (!room.connection?.isOpen && !room.reconnection.isReconnecting)
      )
        return false;
      throw new Error("Timed out securing the current area for travel.");
    } finally {
      room.onLeave.remove(onLeave);
    }
  }

  private async joinRegionTransitionDestination(
    transition: RegionTransitionRequest,
  ): Promise<GameRoom> {
    const deadline =
      (transition.startedAt ?? Date.now()) + REGION_TRANSITION_RETRY_WINDOW_MS;
    let lastError: unknown;
    while (Date.now() < deadline) {
      let entry: RegionTransitionEntryResponse;
      try {
        entry = await this.requestRegionTransitionEntry(transition);
      } catch (error) {
        lastError = error;
        if (!this.isTransientTransitionError(error)) throw error;
        await this.delay(
          Math.min(DESTINATION_RETRY_DELAY_MS, deadline - Date.now()),
        );
        continue;
      }
      try {
        return await this.dependencies
          .createClient()
          .joinOrCreate<InstanceState>(entry.roomName, {
            mode: entry.mode,
            instanceId: entry.instanceId,
            mapId: entry.mapId,
            spawnId: entry.spawnId,
            regionTransitionId: entry.regionTransitionId,
            ticket: entry.ticket,
          });
      } catch (error) {
        lastError = error;
        const message = error instanceof Error ? error.message : String(error);
        if (
          !message.includes("SQUAD_ACTIVE_IN_ANOTHER_INSTANCE") &&
          !this.isTransientTransitionError(error)
        )
          throw error;
        await this.delay(
          Math.min(DESTINATION_RETRY_DELAY_MS, deadline - Date.now()),
        );
      }
    }
    if (lastError instanceof Error) throw lastError;
    throw new Error("The destination transition window has expired.");
  }

  private isTransientTransitionError(error: unknown): boolean {
    const message = error instanceof Error ? error.message : String(error);
    return (
      error instanceof TypeError ||
      /network|fetch failed|timed out|timeout/i.test(message) ||
      /Request failed \(5\d\d\)/.test(message)
    );
  }

  private async closeRoomWithoutReconnect(room: GameRoom): Promise<void> {
    room.reconnection.enabled = false;
    if (!room.connection?.isOpen) return;
    await Promise.race([room.leave(false), this.delay(1_000)]);
  }

  private beginIntentionalRoomExit(room: GameRoom): void {
    const connectionOpen = room.connection?.isOpen ?? false;
    const reconnecting = room.reconnection.isReconnecting;
    console.info("Returning to village from game room", {
      roomId: room.roomId,
      sessionId: room.sessionId,
      connectionOpen,
      reconnecting,
    });

    room.reconnection.enabled = false;
    if (connectionOpen) {
      this.leaveRoomConsented(room);
      return;
    }
    if (!reconnecting) return;

    const removePendingExitListeners = (): void => {
      room.onReconnect.remove(onReconnect);
      room.onLeave.remove(onLeave);
    };
    const onLeave = (): void => removePendingExitListeners();
    const onReconnect = (): void => {
      room.onReconnect.remove(onReconnect);
      queueMicrotask(() => {
        if (!room.connection?.isOpen) {
          removePendingExitListeners();
          return;
        }
        console.info("Completing intentional room exit after reconnection", {
          roomId: room.roomId,
          sessionId: room.sessionId,
        });
        this.leaveRoomConsented(room);
      });
    };

    room.onLeave(onLeave);
    room.onReconnect(onReconnect);
  }

  private leaveRoomConsented(room: GameRoom): void {
    try {
      void room.leave(true).catch((error: unknown) => {
        console.warn("Could not send consented game room leave", {
          roomId: room.roomId,
          sessionId: room.sessionId,
          error,
        });
      });
    } catch (error) {
      console.warn("Could not send consented game room leave", {
        roomId: room.roomId,
        sessionId: room.sessionId,
        error,
      });
    }
  }

  private delay(milliseconds: number): Promise<void> {
    return new Promise((resolve) =>
      window.setTimeout(resolve, Math.max(0, milliseconds)),
    );
  }

  private async mount(
    room: GameRoom,
    mode: GameMode,
    mapId: string | undefined,
  ): Promise<void> {
    this.update(
      PHASER_PROGRESS_START,
      "Loading world resources",
      "Preparing map tiles, character appearances, and combat visuals.",
    );
    await this.dependencies.mountGame(room, mode, mapId, {
      onAssetProgress: (progress) => {
        const normalized = Math.min(1, Math.max(0, progress));
        this.update(
          PHASER_PROGRESS_START +
            normalized * (PHASER_PROGRESS_END - PHASER_PROGRESS_START),
          "Loading world resources",
          `Phaser loaded ${Math.round(normalized * 100)}% of the queued assets.`,
        );
      },
      onFirstFrame: () =>
        this.update(
          0.98,
          "Rendering the game scene",
          "The world is ready; displaying its first frame.",
        ),
    });
    this.update(
      1,
      "Your journey is ready",
      "The game scene has rendered successfully.",
    );
    this.dependencies.loading.hide();
  }

  private waitForMapRuntime(
    room: GameRoom,
    timeoutMs = MAP_RUNTIME_TIMEOUT_MS,
  ): Promise<void> {
    if (this.hasMapRuntime(room.state)) return Promise.resolve();

    return new Promise((resolve, reject) => {
      let settled = false;
      let timeout = 0;
      const cleanup = (): void => {
        window.clearTimeout(timeout);
        room.onStateChange.remove(onStateChange);
        room.onLeave.remove(onLeave);
      };
      const finish = (error?: Error): void => {
        if (settled) return;
        settled = true;
        cleanup();
        if (error) reject(error);
        else resolve();
      };
      const onStateChange = (state: InstanceState): void => {
        if (this.hasMapRuntime(state)) finish();
      };
      const onLeave = (code: number, reason?: string): void => {
        finish(
          new Error(
            `Room left before map state was ready (${code}${reason ? `: ${reason}` : ""}).`,
          ),
        );
      };

      room.onStateChange(onStateChange);
      room.onLeave(onLeave);
      timeout = window.setTimeout(
        () =>
          finish(new Error("Timed out waiting for authoritative map state.")),
        timeoutMs,
      );
      if (this.hasMapRuntime(room.state)) finish();
    });
  }

  private hasMapRuntime(state: InstanceState): boolean {
    return Boolean(state.mapId && state.mapRevision);
  }

  private update(value: number, label: string, detail: string): void {
    const progress: GameLoadingProgress = { value, label, detail };
    this.dependencies.loading.update(progress);
  }

  private formatEntryError(error: unknown): string {
    const serverMessage =
      error instanceof Error ? error.message : String(error);
    return serverMessage.includes("SQUAD_ACTIVE_IN_ANOTHER_INSTANCE")
      ? "Your squad is already active in another field operation. Return to that instance before entering a different playable map or PvP."
      : serverMessage;
  }
}
